import { Img } from "@/components/site/Img";
import { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Play } from "lucide-react";
import { BUILDS } from "@/components/site/buildsData";
import { VideoLightbox } from "./VideoLightbox";

const MEDIA_BASE = `${process.env.REACT_APP_BACKEND_URL}/api/media`;

const [featured, side] = BUILDS;

const INSTALL_STEPS = [
  { step: "01", title: "Dig & grade", body: "Chris digs out the spot and grades it so everything sits level for years." },
  { step: "02", title: "Set the fountain", body: "Base, sealed basin, plumbing, then the fountain goes in piece by piece." },
  { step: "03", title: "Fill & finish", body: "Final finish coat on the pool, water in, pump on." },
];

const BuildLink = ({ id, name }) => (
  <Link
    to={`/builds#${id}`}
    data-testid={`builds-link-${id}`}
    className="inline-flex items-center gap-2 text-[#7A6547] hover:text-[#5E4D35] text-[11px] tracking-[0.22em] uppercase border-b border-[#7A6547]/40 hover:border-[#7A6547] pb-1 transition-colors"
  >
    See how {name} was built
    <ArrowRight size={13} strokeWidth={1.5} />
  </Link>
);

const Photo = ({ photo, className = "" }) => (
  <figure className={className}>
    <div className="w-full aspect-[4/3] overflow-hidden bg-[#D5CEBD]">
      <Img src={photo.src} alt={photo.alt} className="w-full h-full object-cover" loading="lazy" />
    </div>
    <figcaption className="mt-2 text-[10px] tracking-[0.2em] uppercase text-[#6F6A60]">
      {photo.caption}
    </figcaption>
  </figure>
);

const VideoTile = ({ video, onPlay }) => (
  <figure>
    <button
      type="button"
      onClick={onPlay}
      data-testid="fountain-builds-video"
      aria-label={`Play video: ${video.title}`}
      className="group relative block w-full aspect-[4/3] overflow-hidden bg-[#1F1E1C]"
    >
      <Img src={video.poster} alt="" aria-hidden loading="lazy" className="absolute inset-0 w-full h-full object-cover opacity-80 group-hover:opacity-100 transition-opacity duration-500" />
      <span className="absolute inset-0 flex items-center justify-center">
        <span className="w-14 h-14 rounded-full border-2 border-white/80 bg-[#0F0E0D]/40 flex items-center justify-center group-hover:bg-white group-hover:scale-105 transition-all duration-300">
          <Play size={20} strokeWidth={1.25} className="ml-0.5 text-white group-hover:text-[#1F1E1C]" fill="currentColor" />
        </span>
      </span>
    </button>
    <figcaption className="mt-2 text-[10px] tracking-[0.2em] uppercase text-[#6F6A60]">Watch it run</figcaption>
  </figure>
);

const MoreLink = ({ to, testid, icon, children }) => (
  <Link
    to={to}
    data-testid={testid}
    className="inline-flex items-center gap-2 px-4 py-2.5 border border-[#7A6547]/60 bg-[#F9F6F0]/60 text-[#7A6547] hover:bg-[#7A6547] hover:text-[#F9F6F0] hover:border-[#7A6547] text-[11px] tracking-[0.22em] uppercase transition-colors"
  >
    {icon}
    {children}
    <ArrowRight size={13} strokeWidth={1.5} />
  </Link>
);

const InstallStrip = () => (
  <div id="how-its-built" data-testid="install-strip" className="bg-[#E9E2D3] border-y border-[#D5CEBD] scroll-mt-24">
    <div className="max-w-[1400px] mx-auto px-6 md:px-10 lg:px-14 py-14 md:py-20">
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-14">
        <div className="lg:col-span-4">
          <div className="section-eyebrow mb-5">
            <span>How an install goes</span>
          </div>
          <h3 className="font-serif text-3xl md:text-4xl text-[#2C2A28] font-light tracking-tight leading-[1.1]">
            From the first dig to the final fill, Chris handles the whole install in your yard.
          </h3>
          <p className="text-[#5C5955] text-sm md:text-base leading-relaxed mt-4">
            Any finish, matched to your space: deep black, weathered white, custom burgundy, or a color match to your home.
          </p>
        </div>
        <ol className="lg:col-span-8 grid grid-cols-1 sm:grid-cols-3 gap-8 sm:gap-6 lg:pt-2">
          {INSTALL_STEPS.map((s) => (
            <li key={s.step} data-testid={`install-step-${s.step}`} className="flex sm:block gap-5 sm:border-t sm:border-[#7A6547]/40 sm:pt-5">
              <div className="font-serif text-4xl md:text-5xl text-[#7A6547] font-light leading-none sm:mb-4 flex-shrink-0 w-12 sm:w-auto">{s.step}</div>
              <div>
                <div className="font-serif text-xl text-[#2C2A28] mb-1.5">{s.title}</div>
                <p className="text-[#5C5955] text-sm leading-relaxed">{s.body}</p>
              </div>
            </li>
          ))}
        </ol>
      </div>
      <div
        data-testid="custom-work-more-links"
        className="mt-12 md:mt-14 pt-8 border-t border-[#7A6547]/30 flex flex-col sm:flex-row sm:flex-wrap gap-3 sm:gap-4"
      >
        <MoreLink to="/builds#featured-project" testid="custom-work-featured-link" icon={<Play size={12} strokeWidth={1.5} fill="currentColor" />}>
          Watch a full backyard install
        </MoreLink>
        <MoreLink to="/builds#color-matching" testid="custom-work-finishes-link">
          Finishes &amp; color matching
        </MoreLink>
        <MoreLink to="/gallery" testid="custom-work-gallery-link">
          Full gallery of recent work
        </MoreLink>
      </div>
    </div>
  </div>
);

export default function FountainBuilds() {
  const [showVideo, setShowVideo] = useState(false);
  return (
    <section
      id="builds"
      data-testid="fountain-builds-section"
      className="bg-[#F9F6F0] pt-10 md:pt-14 border-t border-[#D5CEBD]"
    >
      <div className="max-w-[1400px] mx-auto px-6 md:px-10 lg:px-14 pb-12 md:pb-16">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-10 md:mb-14">
          <div className="max-w-3xl">
            <div className="section-eyebrow mb-5">
              <span>Custom Work</span>
            </div>
            <h2
              data-testid="fountain-builds-heading"
              className="font-serif font-light text-[#2C2A28] text-4xl md:text-5xl lg:text-6xl leading-[1.05] tracking-tight"
            >
              Built from the ground up, <em className="not-italic text-[#7A6547]">one panel at a time.</em>
            </h2>
          </div>
          <Link
            to="/builds"
            data-testid="fountain-builds-all-link"
            className="inline-flex items-center gap-2 text-[#7A6547] hover:text-[#5E4D35] text-[11px] tracking-[0.22em] uppercase whitespace-nowrap transition-colors"
          >
            All builds
            <ArrowRight size={13} strokeWidth={1.5} />
          </Link>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-8">
          <article data-testid={`build-card-${featured.id}`} className="lg:col-span-8">
            <div className="grid grid-cols-2 gap-4 md:gap-5 mb-5">
              <Photo photo={{ ...featured.completed, caption: featured.justBuilt ? "Just built" : "Completed" }} />
              {featured.video ? <VideoTile video={featured.video} onPlay={() => setShowVideo(true)} /> : <Photo photo={featured.photos[0]} />}
            </div>
            <div className="text-[10px] tracking-[0.32em] uppercase text-[#7A6547] mb-2">{featured.eyebrow}</div>
            <h3 className="font-serif text-2xl md:text-3xl text-[#2C2A28] font-light tracking-tight mb-3">
              {featured.name}
            </h3>
            <p className="text-[#5C5955] text-base leading-relaxed max-w-2xl mb-5">{featured.teaser}</p>
            <BuildLink id={featured.id} name={featured.name} />
          </article>

          <article data-testid={`build-card-${side.id}`} className="lg:col-span-4 lg:border-l lg:border-[#D5CEBD] lg:pl-8">
            <Photo photo={{ ...side.completed, caption: "Completed" }} className="mb-5" />
            <div className="text-[10px] tracking-[0.32em] uppercase text-[#7A6547] mb-2">{side.eyebrow}</div>
            <h3 className="font-serif text-2xl md:text-3xl text-[#2C2A28] font-light tracking-tight mb-3">
              {side.name}
            </h3>
            <p className="text-[#5C5955] text-base leading-relaxed mb-5">{side.teaser}</p>
            <BuildLink id={side.id} name={side.name} />
          </article>
        </div>

      </div>
      <InstallStrip />
      {featured.video && (
        <VideoLightbox
          open={showVideo}
          onClose={() => setShowVideo(false)}
          video={{ mp4: `${MEDIA_BASE}/${featured.video.file}`, poster: featured.video.poster, title: featured.video.title }}
        />
      )}
    </section>
  );
}
